import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { Button, Input } from "../ui";
import { SettingsSection, StatusText } from "./SettingsSection";
import { isLocalOnly } from "../capture/install";
import { IOS_SHORTCUT_URL, setupCode } from "../capture/iosShortcut";

/**
 * Settings → Capture from the phone (I2): how to get thoughts into the tray in two seconds. Android: install
 * the app (QR code → /install), then "Share → Drang". iPhone: the ready-made Shortcut (iCloud link as a QR
 * code), then a setup code (address and a capture-only token) it scans on its first run.
 */
export function CaptureSettings() {
  const { t, i18n } = useTranslation();
  const [token, setToken] = useState<{ code: string; qr: string } | null>(null);
  const [shortcutQr, setShortcutQr] = useState<string | null>(null);
  const showShortcutQr = async () =>
    setShortcutQr(shortcutQr ? null : await QRCode.toDataURL(IOS_SHORTCUT_URL, { margin: 1, width: 200 }));
  const [appQr, setAppQr] = useState<string | null>(null);
  const installUrl = `${location.origin}/install`;
  const showAppQr = async () =>
    setAppQr(appQr ? null : await QRCode.toDataURL(installUrl, { margin: 1, width: 200 }));

  // the setup codes made here: a capture key each, listed and revoked here, not in Access (UI cleanup U0)
  const [codes, setCodes] = useState<{ id: string; createdAt: string; lastSeenAt: string | null }[]>([]);
  const loadCodes = () =>
    fetch("/api/internal/tokens?feature=capture", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { tokens: [] }))
      .then((b: { tokens: typeof codes }) => setCodes(b.tokens));
  useEffect(() => {
    void loadCodes();
  }, []);
  const revoke = async (id: string) => {
    await fetch("/api/internal/tokens", {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, feature: "capture" }),
    });
    void loadCodes();
  };
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });

  const makeToken = async () => {
    const r = await fetch("/api/internal/tokens", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: t("capture.tokenName"), scopes: ["capture:write"], feature: "capture" }),
    });
    if (!r.ok) return;
    const { token: value } = (await r.json()) as { token: string };
    // the setup code: the capture address and the token, read by the shortcut on its first run
    const code = setupCode(location.origin, value);
    setToken({ code, qr: await QRCode.toDataURL(code, { margin: 1, width: 240 }) });
    void loadCodes();
  };

  const step = "text-[13px] leading-relaxed";
  return (
    <SettingsSection
      id="settings-capture"
      intro={t("capture.settingsHint")}
      title={t("capture.settingsTitle")}
      testId="settings-capture"
      folded
    >
      <h3 className="text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">Android</h3>
      <div className="flex flex-col gap-2">
        <div>
          <Button size="sm" variant="secondary" onClick={() => void showAppQr()} data-testid="capture-app-qr">
            {appQr ? t("capture.hideQr") : t("capture.showQr")}
          </Button>
        </div>
        {appQr && (
          <div className="flex flex-wrap items-start gap-4" data-testid="capture-app-qr-code">
            <img
              src={appQr}
              alt={t("capture.appQrAlt")}
              width={140}
              height={140}
              className="rounded-sm bg-white p-1"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1 text-[12.5px]">
              <span>{t("capture.appQrHint")}</span>
              <span className="font-mono text-[12px] text-neutral-500">{installUrl}</span>
              {isLocalOnly(location.hostname) && (
                <StatusText tone="warn" testId="capture-app-qr-local">
                  {t("capture.appQrLocal")}
                </StatusText>
              )}
            </div>
          </div>
        )}
      </div>
      <ol className={`list-decimal pl-5 ${step}`}>
        <li>{t("capture.android1")}</li>
        <li>{t("capture.android2")}</li>
        <li>{t("capture.android3")}</li>
      </ol>

      <h3 className="mt-2 text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">iPhone</h3>
      <ol className={`list-decimal pl-5 ${step}`} data-testid="capture-ios">
        <li>
          {IOS_SHORTCUT_URL ? (
            <>
              {t("capture.iosAdd")}{" "}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void showShortcutQr()}
                data-testid="capture-ios-shortcut"
              >
                {shortcutQr ? t("capture.hideQr") : t("capture.iosShortcutQr")}
              </Button>
              {shortcutQr && (
                <img
                  src={shortcutQr}
                  alt={t("capture.iosShortcutQrAlt")}
                  width={140}
                  height={140}
                  className="mt-2 block rounded-sm bg-white p-1"
                />
              )}
            </>
          ) : (
            <span data-testid="capture-ios-unpublished">{t("capture.iosUnpublished")}</span>
          )}
        </li>
        <li>
          {t("capture.iosSetup")}{" "}
          {!token && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void makeToken()}
              data-testid="capture-make-token"
            >
              {t("capture.makeSetupCode")}
            </Button>
          )}
          {token && (
            <div className="mt-2 flex flex-wrap items-start gap-4" data-testid="capture-token">
              <img
                src={token.qr}
                alt={t("capture.qrAlt")}
                width={160}
                height={160}
                className="rounded-sm bg-white p-1"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-[12px] text-neutral-500">{t("capture.tokenOnce")}</span>
                <Input
                  compact
                  readOnly
                  value={token.code}
                  onFocus={(e) => e.target.select()}
                  className="font-mono"
                  aria-label={t("capture.setupCode")}
                  data-testid="capture-setup-code"
                />
                {isLocalOnly(location.hostname) && (
                  <StatusText tone="warn">{t("capture.appQrLocal")}</StatusText>
                )}
              </div>
            </div>
          )}
        </li>
        <li>{t("capture.iosRunOnce")}</li>
        <li>{t("capture.iosUse")}</li>
      </ol>
      {codes.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="capture-codes">
          <h3 className="text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">
            {t("capture.codesTitle")}
          </h3>
          <ul className="flex flex-col gap-1">
            {codes.map((c) => (
              <li key={c.id} className="flex items-center gap-3 text-[13px]" data-testid="capture-code">
                <span>{t("capture.codeMade", { when: fmt.format(new Date(c.createdAt)) })}</span>
                <span className="text-[12px] text-neutral-500">
                  {c.lastSeenAt
                    ? t("access.lastSeen", { when: fmt.format(new Date(c.lastSeenAt)) })
                    : t("access.never")}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void revoke(c.id)}
                  aria-label={t("capture.codeRevoke")}
                  data-testid="capture-code-revoke"
                >
                  {t("access.revoke")}
                </Button>
              </li>
            ))}
          </ul>
          <p className="text-[12px] text-neutral-500">{t("capture.codesHint")}</p>
        </div>
      )}
      <p className="text-[12px] text-neutral-500">{t("capture.reachHint")}</p>
    </SettingsSection>
  );
}
